import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Check, AlertCircle } from 'lucide-react';
import { Task, Client, Depot } from '../../types';
import { supabase } from '../../lib/supabase';
import SearchableSelect from '../SearchableSelect';

interface ClientLocation {
  id: string;
  name: string;
  position_lat: number;
  position_lng: number;
  is_primary: boolean;
  client_id: string;
}

interface CompleteTaskModalProps {
  task: Task;
  clients: Client[];
  depots: Depot[];
  onConfirm: (data: {
    actualLiters?: number;
    actualQuantity?: number;
    dropLocationType?: 'client' | 'depot';
    dropLocationId?: string;
    endTime: string;
    comment?: string;
  }) => void;
  onCancel: () => void;
}

const CompleteTaskModal: React.FC<CompleteTaskModalProps> = ({
  task,
  clients,
  depots,
  onConfirm,
  onCancel
}) => {
  const [formData, setFormData] = useState({
    actualLiters: task.type === 'fuel' ? String(task.plannedLiters || '') : '',
    actualQuantity: task.type === 'dumpster' ? '0' : '',
    dropLocationType: '' as 'client' | 'depot' | '',
    selectedClientId: '',
    selectedClientLocationId: '',
    selectedDepotId: '',
    endTime: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    comment: task.comment || ''
  });

  const [clientLocationsForSelection, setClientLocationsForSelection] = useState<ClientLocation[]>([]);
  const [products, setProducts] = useState<any[]>([]);

  // Load products for phyto and engrais tasks
  useEffect(() => {
    if (task.type === 'phyto' || task.type === 'engrais') {
      const loadProducts = async () => {
        try {
          const tableName = task.type === 'engrais' ? 'engrais_products' : 'phyto_products';
          const { data, error } = await supabase
            .from(tableName)
            .select('*')
            .eq('task_id', task.id);

          if (error) throw error;

          setProducts(data || []);
        } catch (error) {
          console.error('Error loading products:', error);
          alert('Erreur lors du chargement des produits');
        }
      };

      loadProducts();
    }
  }, [task.id, task.type]);

  // Load client locations when a client is selected
  useEffect(() => {
    if (formData.selectedClientId) {
      const loadClientLocations = async () => {
        try {
          const { data, error } = await supabase
            .from('client_locations')
            .select('*')
            .eq('client_id', formData.selectedClientId)
            .order('is_primary', { ascending: false });

          if (error) throw error;

          setClientLocationsForSelection(data || []);

          // Auto-select primary location if available
          const primaryLocation = data?.find(loc => loc.is_primary);
          if (primaryLocation) {
            setFormData(prev => ({ ...prev, selectedClientLocationId: primaryLocation.id }));
          } else {
            setFormData(prev => ({ ...prev, selectedClientLocationId: '' }));
          }
        } catch (error) {
          console.error('Error loading client locations:', error);
          alert('Erreur lors du chargement des points GPS du client');
        }
      };

      loadClientLocations();
    } else {
      setClientLocationsForSelection([]);
      setFormData(prev => ({ ...prev, selectedClientLocationId: '' }));
    }
  }, [formData.selectedClientId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const data: any = {
      endTime: formData.endTime,
      comment: formData.comment.trim() || undefined
    };

    if (task.type === 'fuel') {
      data.type = 'fuel';
      data.actualLiters = parseInt(formData.actualLiters);
    } else if (task.type === 'dumpster') {
      data.type = 'dumpster';
      if (formData.actualQuantity && parseFloat(formData.actualQuantity) > 0) {
        data.actualQuantity = parseFloat(formData.actualQuantity);
      }
      if (formData.dropLocationType) {
        let dropLocationId = '';
        let position = undefined;
        
        if (formData.dropLocationType === 'client' && formData.selectedClientLocationId) {
          dropLocationId = formData.selectedClientLocationId;
          const selectedLocation = clientLocationsForSelection.find(loc => loc.id === formData.selectedClientLocationId);
          if (selectedLocation) {
            position = {
              lat: selectedLocation.position_lat,
              lng: selectedLocation.position_lng
            };
          }
        } else if (formData.dropLocationType === 'depot' && formData.selectedDepotId) {
          dropLocationId = formData.selectedDepotId;
          const selectedDepot = depots.find(d => d.id === formData.selectedDepotId);
          if (selectedDepot) {
            position = selectedDepot.position;
          }
        }
        
        if (dropLocationId && position) {
          data.dropLocation = {
            type: formData.dropLocationType,
            id: dropLocationId,
            position
          };
        }
      }
    } else if (task.type === 'phyto') {
      data.type = 'phyto';
      data.products = products.map(p => ({
        name: p.name,
        actualQuantity: p.actual_quantity
      }));
    } else if (task.type === 'engrais') {
      data.type = 'engrais';
      data.products = products.map(p => ({
        name: p.name,
        actualQuantityKg: p.actual_quantity_kg
      }));
    }

    onConfirm(data);
  };

  // Prepare client options for SearchableSelect
  const clientOptions = clients.map(client => ({
    value: client.id,
    label: `${client.companyName} (${client.firstName} ${client.lastName})`
  }));

  // Prepare client location options
  const clientLocationOptions = clientLocationsForSelection.map(location => ({
    value: location.id,
    label: `${location.name}${location.is_primary ? ' (Principal)' : ''}`
  }));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        <div className="flex items-center space-x-2 text-blue-600 mb-4">
          <AlertCircle className="h-6 w-6" />
          <h2 className="text-xl font-bold">Terminer la tâche</h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Date et heure de fin
              </label>
              <input
                type="datetime-local"
                value={formData.endTime}
                onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {task.type === 'fuel' && (
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Litrage effectif
                </label>
                <input
                  type="number"
                  value={formData.actualLiters}
                  onChange={(e) => setFormData(prev => ({ ...prev, actualLiters: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  min="1"
                />
              </div>
            )}

            {task.type === 'phyto' && (
              <div className="space-y-3">
                <label className="block text-gray-700 font-medium mb-2">
                  Quantités effectives
                </label>
                {products.map((product, index) => (
                  <div key={index}>
                    <label className="block text-sm text-gray-600 mb-1">
                      {product.name} (Prévu: {product.planned_quantity})
                    </label>
                    <input
                      type="number"
                      value={product.actual_quantity || ''}
                      onChange={(e) => {
                        const newProducts = [...products];
                        newProducts[index] = { ...newProducts[index], actual_quantity: parseInt(e.target.value) || 0 };
                        setProducts(newProducts);
                      }}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      min="0"
                    />
                  </div>
                ))}
              </div>
            )}

            {task.type === 'engrais' && (
              <div className="space-y-3">
                <label className="block text-gray-700 font-medium mb-2">
                  Quantités effectives (kg)
                </label>
                {products.map((product, index) => (
                  <div key={index}>
                    <label className="block text-sm text-gray-600 mb-1">
                      {product.name} (Prévu: {product.planned_quantity_kg} kg)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={product.actual_quantity_kg || ''}
                      onChange={(e) => {
                        const newProducts = [...products];
                        newProducts[index] = { ...newProducts[index], actual_quantity_kg: parseFloat(e.target.value) || 0 };
                        setProducts(newProducts);
                      }}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      min="0"
                    />
                  </div>
                ))}
              </div>
            )}

            {task.type === 'dumpster' && (
              <>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Quantité effective (tonnes)
                  </label>
                  <input
                    type="number"
                    value={formData.actualQuantity}
                    onChange={(e) => setFormData(prev => ({ ...prev, actualQuantity: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="0.001"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Type de destination
                  </label>
                  <select
                    value={formData.dropLocationType}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      dropLocationType: e.target.value as '' | 'client' | 'depot',
                      selectedClientId: '', // Reset selections when type changes
                      selectedClientLocationId: '',
                      selectedDepotId: ''
                    }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Sélectionner...</option>
                    <option value="client">Client</option>
                    <option value="depot">Dépôt</option>
                  </select>
                </div>

                {formData.dropLocationType === 'client' && (
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Client
                    </label>
                    <SearchableSelect
                      options={clientOptions}
                      value={formData.selectedClientId}
                      onChange={(value) => setFormData(prev => ({ ...prev, selectedClientId: value }))}
                      placeholder="Rechercher un client..."
                      searchPlaceholder="Tapez pour rechercher..."
                    />
                  </div>
                )}

                {formData.dropLocationType === 'client' && formData.selectedClientId && clientLocationsForSelection.length > 0 && (
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Point GPS
                    </label>
                    <SearchableSelect
                      options={clientLocationOptions}
                      value={formData.selectedClientLocationId}
                      onChange={(value) => setFormData(prev => ({ ...prev, selectedClientLocationId: value }))}
                      placeholder="Sélectionner un point GPS..."
                      searchPlaceholder="Rechercher un point..."
                    />
                  </div>
                )}

                {formData.dropLocationType === 'depot' && (
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Dépôt
                    </label>
                    <select
                      value={formData.selectedDepotId}
                      onChange={(e) => setFormData(prev => ({ ...prev, selectedDepotId: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Sélectionner un dépôt...</option>
                      {depots.map(depot => (
                        <option key={depot.id} value={depot.id}>
                          {depot.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}

            {/* Commentaire (facultatif) */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Commentaire (facultatif)
              </label>
              <textarea
                value={formData.comment}
                onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Informations complémentaires..."
                maxLength={500}
              />
              <div className="text-xs text-gray-500 mt-1 text-right">
                {formData.comment.length}/500 caractères
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border rounded-lg"
            >
              Annuler
            </button>
            
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-lg"
            >
              <Check className="h-4 w-4 mr-2 inline" />
              Terminer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompleteTaskModal;